import React, { useEffect, useState } from "react";
import { fetchMonthlyTurnover } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const MonthlyTurnoverChart = ({ date }) => {
  const [data, setData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const { currencySymbol, formatCurrency } = useCustomerCurrency();
  const formatAxisValue = (value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
  };

  useEffect(() => {
    if (!date) {
      setData([]);
      setWorkcellNames({});
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchMonthlyTurnover(date)
      .then((apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          setWorkcellNames({});
          setData([]);
          return;
        }
        // Get unique workcell IDs
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);
        const days = getMonthDays(date);
        const fullMonthData = days.map((day) => {
          const entry = { day };
          workcells.forEach((workcell) => { entry[workcell] = 0; });
          return entry;
        });
        apiData.forEach((entry) => {
          const index = fullMonthData.findIndex((d) => d.day === entry.day);
          if (index !== -1) {
            fullMonthData[index][entry.workcell_id] = entry.total_turnover;
          }
        });
        setData(fullMonthData);
      })
      .catch(() => {
        setWorkcellNames({});
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date]);

  // Compute all workcell IDs sorted numerically for color mapping
  const allWorkcellIds = data.length > 0
    ? Object.keys(data[0] || {})
        .filter(key => key !== "day")
        .map(Number)
        .sort((a, b) => a - b)
    : [];

  const sortedWorkcells = data.length > 0
    ? Object.keys(data[0] || {})
        .filter(key => key !== "day")
        .sort((a, b) => {
          const totalA = data.reduce((sum, entry) => sum + (entry[a] || 0), 0);
          const totalB = data.reduce((sum, entry) => sum + (entry[b] || 0), 0);
          return totalB - totalA;
        })
    : [];

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (data.length === 0) return <div>{t("common.noData")}</div>;

  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const date = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
          {date.toLocaleString(locale, { month: 'long' })}
        </text>
      </g>
    ) : null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        {/* Main X axis: days */}
        <XAxis
          dataKey="day"
          tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
          height={30}
          axisLine={true}
          tickLine={true}
        />
        {/* Secondary X axis: month name */}
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          height={20}
          xAxisId="month"
          tick={MonthTick}
          interval={0}
          allowDataOverflow={true}
          orientation="bottom"
        />
        <YAxis
          width={96}
          tickFormatter={formatAxisValue}
          label={{
            value: t("monthly.overview.turnoverChart.axisLabel", { currency: currencySymbol }),
            angle: -90,
            position: "insideLeft",
            dx: -8,
            style: { textAnchor: "middle" }
          }}
        />
        <Tooltip formatter={(value) => formatCurrency(value)} />
        {sortedWorkcells.map((workcell) => (
          <Bar
            key={workcell}
            dataKey={workcell}
            name={workcellNames[workcell]}
            stackId="a"
            fill={getWorkcellColor(workcell, theme.palette.chart.colors, allWorkcellIds)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MonthlyTurnoverChart;
